import { PropertyStatus } from "../models/property";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment/moment";

export function isPropertyDisabled(property) {
  if (!property) return false;
  return (
    property.abonnement_actif === false &&
    property.status === PropertyStatus.Actif.status
  );
}

function computeLoyerBreakdown(obj, loyer_hc) {
  const taux_loyer = obj.tva ? (obj.taux_tva || 20) / 100 : 0;
  const taux_charges = obj.tva_charge
    ? (obj.taux_tva_charge || obj.taux_tva || 20) / 100
    : 0;
  const hc = loyer_hc * (1 + taux_loyer);
  const charges = (obj.charges || 0) * (1 + taux_charges);
  let total = hc + charges;
  obj.autre_appel.forEach((appel) => {
    const taux_appel = appel.tva ? (obj.taux_tva || 20) / 100 : 0;
    const sign = appel.remboursement ? -1 : 1;
    total += sign * Math.abs(appel.montant) * (1 + taux_appel);
  });

  return { hc, charges, total };
}

export function getTotalLoyer(prop) {
  return getLoyerBreakdown(prop).total;
}

function getLoyerBreakdown(prop) {
  return computeLoyerBreakdown(
    prop,
    prop.loyer_hc + (prop.complement_loyer || 0),
  );
}

// tenant.loyer_hc inclut déjà le complément de loyer (fondu à la génération du
// bail), contrairement à property.loyer_hc qui est le loyer de base.
export function getTotalLoyerTenant(tenant) {
  return computeLoyerBreakdown(tenant, tenant.loyer_hc).total;
}

function findCurrentTenant(property, tenants) {
  return tenants.find(
    (ten) =>
      ten.id_bien === property.id &&
      ten.status === 1 &&
      moment().isBetween(ten.debut, ten.fin),
  );
}

// Montants TTC du bail en cours s'il y en a un, sinon ceux du bien.
// `total` inclut aussi les autres appels (donc pas forcément hc + charges).
export function getEffectivePropertyLoyerBreakdown(property, tenants) {
  const locataireActuel = findCurrentTenant(property, tenants);
  return locataireActuel
    ? computeLoyerBreakdown(locataireActuel, locataireActuel.loyer_hc)
    : getLoyerBreakdown(property);
}

export function getEffectivePropertyLoyer(property, tenants) {
  return getEffectivePropertyLoyerBreakdown(property, tenants).total;
}

export function buildPropertyFoldersTree(propertyFolders, properties) {
  const propertyFoldersById = Object.fromEntries(
    propertyFolders.map((pf) => {
      pf.properties = [];
      pf.property_folders = [];
      return [pf.id, pf];
    }),
  );
  for (let property of properties) {
    if (property.id_property_folder) {
      propertyFoldersById[property.id_property_folder]?.properties.push(
        property,
      );
    }
  }
  for (let pf of propertyFolders) {
    if (pf.id_parent) {
      propertyFoldersById[pf.id_parent]?.property_folders.push(pf);
    }
  }
  return cloneDeep({
    property_folders: propertyFolders.filter((pf) => !pf.id_parent),
    properties: properties.filter((p) => !p.id_property_folder),
  });
}

export function findPropertyFolder(propertyFolders, id) {
  if (!propertyFolders || propertyFolders.length === 0) return null;
  return (
    propertyFolders.find((pf) => pf.id === id) ??
    propertyFolders
      .map((pf) => findPropertyFolder(pf.property_folders, id))
      .find((pf) => !!pf)
  );
}

export function filterPropertiesAndFoldersByStatus(
  propertiesAndFolders,
  status,
) {
  if (propertiesAndFolders === null) return null;

  const filtered = { ...propertiesAndFolders };
  filtered.properties =
    filtered.properties
      ?.filter((p) => p.status === status)
      .sort((p1, p2) =>
        p1.abonnement_actif === p2.abonnement_actif
          ? 0
          : p1.abonnement_actif
          ? -1
          : 1,
      ) ?? [];

  // Quand on cherche les actifs, si un sous-dossier est archivé, on sait deja qu'il n'a pas d'enfant actif
  // On peut deja filtrer tous les sous-dossiers qui n'ont pas le status spécifié
  if (status === PropertyStatus.Actif.status) {
    filtered.property_folders =
      filtered.property_folders?.filter((pf) => pf.status === status) ?? [];
  }

  // Dans les autres cas, on filtre recursivement les sous-dossiers
  filtered.property_folders =
    filtered.property_folders
      ?.map((pf) => filterPropertiesAndFoldersByStatus(pf, status))
      .filter((pf) => pf !== null) ?? [];

  // S'il n'y a aucun bien ou sous-dossier avec le status spécifié, et que son status n'est pas le bon,
  // on retourne null pour qu'il n'apparaisse pas
  if (
    filtered.property_folders.length === 0 &&
    filtered.properties.length === 0 &&
    filtered.status !== undefined &&
    filtered.status !== status
  ) {
    return null;
  }

  return filtered;
}

export function getIncludedPropertyAndFolderIds(propertyFolder) {
  const res = { propertyIds: new Set(), folderIds: new Set() };
  if (!propertyFolder) return res;

  res.propertyIds = new Set([...propertyFolder.properties.map((p) => p.id)]);
  res.folderIds.add(propertyFolder.id);

  for (let folder of propertyFolder.property_folders) {
    const { propertyIds, folderIds } = getIncludedPropertyAndFolderIds(folder);
    propertyIds.forEach((id) => res.propertyIds.add(id));
    folderIds.forEach((id) => res.folderIds.add(id));
  }

  return res;
}
